import '../../modal/components/modal/modal.component.scss';
import './membership-modal-extensions.scss';

import type { FunctionComponent } from 'preact';
import { useCallback, useMemo } from 'preact/hooks';

import { useTranslation } from '../../i18n';
import type { Membership } from '../models/membership.model';
import { formatDate } from '../utils/membership.formatters';
import { getMembershipLabel } from '../utils/membership.utlis';

export interface MembershipConfirmCancellationModalProps {
  membership: Membership;
  title?: string;
  message?: string;
  onClose: (confirmed: boolean) => void;
}

export const MembershipConfirmCancellationModal: FunctionComponent<
  MembershipConfirmCancellationModalProps
> = ({ membership, title, message, onClose }) => {
  const { t } = useTranslation();

  // Memoize membership label
  const membershipLabel = useMemo(
    () => getMembershipLabel(membership),
    [membership.accountSettings?.membershipLabel]
  );

  const membershipLabelLowercase = useMemo(() => membershipLabel.toLowerCase(), [membershipLabel]);

  // Check if it's a subscription (vs membership)
  const isSubscription = useMemo(
    () => membership?.accountSettings?.membershipLabel === 'SUBSCRIPTION',
    [membership.accountSettings?.membershipLabel]
  );

  // Memoized event handlers
  const handleCancel = useCallback(() => {
    onClose(false);
  }, [onClose]);

  const handleConfirm = useCallback(() => {
    onClose(true);
  }, [onClose]);

  return (
    <div className="single-membership-confirm-cancellation-modal">
      <div className="single-modal-header">
        <h3 className="single-modal-title">
          {title && title !== '' ? title : t('membership.cancelTitle', { label: membershipLabel })}
        </h3>
      </div>
      <div className="single-modal-body">
        {message && message !== '' ? (
          <p dangerouslySetInnerHTML={{ __html: message }} />
        ) : (
          <>
            <p>
              <b>{t('membership.areYouSureCancel')}</b>
              <br />
              {t('membership.cancellationEndOfTerm')}
              {!isSubscription && (
                <> {t('membership.stillHaveAccess', { label: membershipLabelLowercase })}</>
              )}
            </p>
            {membership?.nextBillingDate && (
              <p>
                <span className="bold">
                  {t('membership.expirationDate', { label: membershipLabel })}
                </span>{' '}
                {formatDate(membership.nextBillingDate)}
              </p>
            )}
            <p className="single-membership-cancel-notice">
              <b>{t('membership.notice')}</b>{' '}
              {t('membership.cancellationNotice', { label: membershipLabel })}
              {membership?.whiteLabel?.enabled &&
                membership?.whiteLabel?.supportEmail &&
                membership.whiteLabel.supportEmail !== '' && (
                  <>
                    {' '}
                    {t('membership.contactForQuestions', { label: membershipLabelLowercase })}{' '}
                    <b>{membership.whiteLabel.supportEmail}</b>.
                  </>
                )}{' '}
              {t('membership.contactIfUnsure')}
            </p>
          </>
        )}
      </div>
      <div className="single-modal-footer single-action-list inline--btns">
        <button className="btn button" onClick={handleCancel}>
          {t('membership.noGoBack')}
        </button>
        <button className="btn button btn-red" onClick={handleConfirm}>
          {t('membership.yesCancel')}
        </button>
      </div>
    </div>
  );
};
